import { useCallback, useEffect, useState } from "react";

export function usePwaUpdate() {
  const [updateReady, setUpdateReady] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined" || !("serviceWorker" in navigator)) {
      return;
    }

    let disposed = false;
    let registrationRef: ServiceWorkerRegistration | null = null;
    let installingWorker: ServiceWorker | null = null;
    const hadInitialController = Boolean(navigator.serviceWorker.controller);

    const markReady = () => {
      if (!disposed) {
        setDismissed(false);
        setUpdateReady(true);
      }
    };

    const handleControllerChange = () => {
      if (hadInitialController) {
        markReady();
      }
    };

    const handleStateChange = () => {
      if (
        installingWorker?.state === "installed" &&
        navigator.serviceWorker.controller
      ) {
        markReady();
      }
    };

    const handleUpdateFound = () => {
      if (!registrationRef?.installing) {
        return;
      }

      if (installingWorker) {
        installingWorker.removeEventListener("statechange", handleStateChange);
      }

      installingWorker = registrationRef.installing;
      installingWorker.addEventListener("statechange", handleStateChange);
    };

    navigator.serviceWorker.addEventListener(
      "controllerchange",
      handleControllerChange,
    );

    void navigator.serviceWorker.getRegistration().then((registration) => {
      if (disposed || !registration) {
        return;
      }

      registrationRef = registration;
      if (registration.waiting) {
        markReady();
      }

      registration.addEventListener("updatefound", handleUpdateFound);
    });

    return () => {
      disposed = true;
      navigator.serviceWorker.removeEventListener(
        "controllerchange",
        handleControllerChange,
      );
      registrationRef?.removeEventListener("updatefound", handleUpdateFound);
      installingWorker?.removeEventListener("statechange", handleStateChange);
    };
  }, []);

  useEffect(() => {
    if (!updateReady) {
      return;
    }

    const timer = window.setTimeout(() => {
      setDismissed(true);
    }, 300000);

    return () => window.clearTimeout(timer);
  }, [updateReady]);

  const reloadApp = useCallback(() => {
    window.location.reload();
  }, []);

  const dismissUpdate = useCallback(() => {
    setDismissed(true);
  }, []);

  return {
    updateReady: updateReady && !dismissed,
    hasUpdateAvailable: updateReady,
    reloadApp,
    dismissUpdate,
  };
}
